import { useEffect, useState } from "react";
import { CustomDrawer } from "../../../../../common/components/Drawer/CustomDrawer";
import { LFTableV3 } from "../../../../../common/components/LFTable/LFTableV3";
import { Loader } from "../../../../../common/components/Loader/Loader";
import { ColumnItem } from "../../../../../common/models/table";
import { ActionsKey } from "../../../../../common/models/Actions";
import { HatchCoverService } from "../../../core/_request";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  useHatchCoverListState,
  useHatchCoverStore,
} from "../../../store/ShiftingStore";
import HatchCoverForm from "./components/HatchCoverForm";
import { useLocation } from "react-router";
import HatchCoverDetails from "./components/HatchCoverDetails";
import { Tag } from "antd";
import { getShiftingStatusTagData } from "../../../../../utilities/StatusShiftingTag";
dayjs.extend(utc);

const cols: ColumnItem[] = [
  {
    key: "state",
    title: "Etat",
    dataIndex: "state",
    show: true,
    render: (state: any) => {
      const { label, color } = getShiftingStatusTagData(state?.id);
      return (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <Tag color={color}>{label}</Tag>
        </div>
      );
    },
  },
  {
    key: "hatchCoverAction",
    title: "Action",
    dataIndex: "hatchCoverAction",
    show: true,
    render: (value: any) => value?.name,
  },
  {
    key: "pannelPosition",
    title: "Panneau",
    dataIndex: "pannelPosition",
    show: true,
    render: (value: any) => value?.name,
  },
  {
    key: "caleNumber",
    title: "N° de cale",
    dataIndex: "caleNumber",
    isCanSort: true,
    show: true,
  },
  {
    key: "bayNumber",
    title: "N° bay",
    dataIndex: "bayNumber",
    isCanSort: true,
    show: true,
  },
  {
    key: "pannelType",
    title: "Type de panneau",
    dataIndex: "pannelType",
    show: true,
    render: (value: any) => value?.name,
  },
  {
    key: "tonnage",
    title: "Tonnage (T)",
    dataIndex: "tonnage",
    isCanSort: true,
    show: true,
  },
  {
    key: "startAt",
    title: "Date",
    dataIndex: "startAt",
    render: (value: any) =>
      value ? dayjs(value, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YY ") : "-",
    isCanSort: false,
    show: true,
  },
  {
    key: "startAt",
    title: "Heure de début",
    dataIndex: "startAt",
    render: (value: any) =>
      value ? dayjs(value, "DD/MM/YYYY HH:mm:ss").format("HH:mm") : "-",
    isCanSort: false,
    show: true,
  },
  {
    key: "endAt",
    title: "Heure de fin",
    dataIndex: "endAt",
    render: (value: any) =>
      value ? dayjs(value, "DD/MM/YYYY HH:mm:ss").format("HH:mm") : "-",
    isCanSort: false,
    show: true,
  },
  {
    key: "isBargeUsed",
    title: "Chaland",
    dataIndex: "isBargeUsed",
    isCanSort: true,
    show: true,
    render: (value: boolean) => value ? "OUI" : "NON",
  },
  {
    key: "bargeMaterial",
    title: "Référence chaland",
    dataIndex: "bargeMaterial",
    show: true,
    render: (value: any) => value ? value?.reference : "-",
  },
  {
    key: "duration",
    title: "Durée (heures)",
    dataIndex: "duration",
    show: true,
  },
];

function HatchCoverList() {
  const { setShiftingList, shiftingList, actioned, totalItems, setActioned } =
    useHatchCoverStore();
  const { pageState, setPageState, resetPageState } = useHatchCoverListState();

  const [isShowForm, setIsShowForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const movementId = queryParams.get("movment");

  useEffect(() => {
    fetchAllHatchCovers({ "movement.id": movementId, ...pageState });
  }, [pageState, movementId]);

  useEffect(() => {
    if (!isShowForm) {
      setActioned({ action: "no", data: null });
    }
  }, [isShowForm]);

  const onActionClick = (item: any, action: ActionsKey) => {
    const id = parseInt(item?.["@id"]?.split("/").pop() || "", 10);

    if (action === "delete") {
      onDeleteHandler(id);
    } else {
      getOne(id, action);
    }
  };

  const onShowAddHandler = () => {
    setIsShowForm((prev: boolean) => !prev);
  };

  const getOne = async (id: number, action: any) => {
    try {
      setIsLoading(true);
      const res = await HatchCoverService.getHatchCoverById(id);
      if (res?.status === HttpStatusCode.Ok) {
        setActioned({
          data: {
            ...res?.data,
          },
          action,
        });
      }
      onShowAddHandler();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteHandler = async (id: number) => {
    try {
      setIsLoading(true);
      const res = await HatchCoverService.deleteHatchCover(id);
      if (res?.status === HttpStatusCode.NoContent) {
        fetchAllHatchCovers({ "movement.id": movementId, ...pageState });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllHatchCovers = async (params?: any) => {
    try {
      setIsLoading(true);
      const res = await HatchCoverService.getAllHatchCovers(params);
      if (res?.status === HttpStatusCode.Ok) {
        setShiftingList(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishAction = () => {
    onShowAddHandler();
    setPageState({ ...pageState });
  };

  const handleSetParams = (params: any) => {
    setPageState({
      ...params,
    });
  };

  return (
    <>
      <Loader isLoading={isLoading} />

      <LFTableV3
        columns={cols}
        params={pageState}
        data={shiftingList}
        onAction={onActionClick}
        isCanDelete={() => true}
        isCanEdit={() => true}
        isCanShow={() => true}
        onSearch={handleSetParams}
        onSort={handleSetParams}
        onResetSearch={resetPageState}
        onChangePage={handleSetParams}
        pagination={{
          defaultCurrentPage: pageState.page,
          itemsPerPage: pageState?.itemsPerPage,
          totalItems: totalItems,
        }}
      />

      {/* Drawer section  */}
      {isShowForm ? (
        <CustomDrawer
          isOpen={isShowForm}
          onClose={onShowAddHandler}
          title={actioned?.action === "update" ? "Modification" : "Détails"}
          width={"75%"}
        >
          {actioned?.action === "update" ? (
            <HatchCoverForm
              onFinish={onFinishAction}
              {...{
                initialValue: actioned?.data,
              }}
            />
          ) : (
            <HatchCoverDetails
              {...(actioned?.action === "read"
                ? {
                    initialValue: actioned?.data,
                  }
                : {})}
            />
          )}
        </CustomDrawer>
      ) : (
        ""
      )}
    </>
  );
}

export default HatchCoverList;
